
import React from "react";
import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, Copy, ArrowRight } from "lucide-react";
import Layout from "../components/Layout";
import Header from "../components/Header";
import PageTransition from "../components/PageTransition";
import { toast } from "@/hooks/use-toast";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useSelector } from "react-redux";
import { RootState } from "@/store";

const OrderConfirmation = () => {
  const [searchParams] = useSearchParams();
  const videoState = useSelector((state: RootState) => state.video);

  // Create safe accessor functions for actor properties
  const getActorName = () => videoState.actor?.name || 'Selected Actor';
  const getActorAvatarUrl = () => videoState.actor?.avatarUrl || '';
  const getActorStyle = () => videoState.actor?.style || 'Standard';

  const orderNumber = searchParams.get("order") || "Unknown";
  const email = searchParams.get("email") || "";
  const isNewAccount = searchParams.get("newAccount") === "true";

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      description: "The order number has been copied to your clipboard.",
    });
  };

  return (
    <Layout>
      <PageTransition>
        <Header />

        <motion.div
          className="space-y-6"
          initial={{
            opacity: 0,
            y: 20,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            delay: 0.2,
            duration: 0.5,
          }}
        >
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="font-bold tracking-tight mb-3 text-xl">Order Confirmed!</h2>
            <p className="text-muted-foreground text-balance">
              Your video is now in production and will be available soon.
            </p>
          </div>

          <div className="glass rounded-xl p-6 space-y-5">
            <div className="border-t border-border pt-5">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-medium">Order Summary</h3>
                  <p className="text-sm text-muted-foreground">Professional Property Video Ad</p>
                </div>
                <span className="font-medium">$99.00</span>
              </div>
            </div>

            {isNewAccount && (
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 text-sm">
                <p className="font-medium text-primary mb-1">Account Created</p>
                <p className="text-muted-foreground">
                  We've created an account for you using {email}. Check your email for login instructions.
                </p>
              </div>
            )}

            <div className="border-t border-border pt-5 space-y-4">
              <div className="space-y-2">
                <h3 className="font-medium">What happens next?</h3>
                <p className="text-sm text-muted-foreground">
                  Your video is now in production and will be ready within 24-48 hours. You'll receive an email notification when it's ready to download.
                </p>
              </div>

              <div className="flex justify-center pt-8">
                <Link to="/dashboard" className="inline-flex items-center text-primary font-medium">
                  View in Dashboard <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </PageTransition>
    </Layout>
  );
};

export default OrderConfirmation;
